// src/component/CouponPaymentBox.js
// クーポンコード入力 → 有効なら決済UIを表示するコンポーネント
// PaymentPhases2026 と違い、管理画面の visibility には縛られない
import React, { useEffect, useState, useContext, useRef } from "react";
import {
  doc,
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../api/firebase";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Collapse,
  Stack,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import LocalActivityRoundedIcon from "@mui/icons-material/LocalActivityRounded";
import CelebrationIcon from "@mui/icons-material/Celebration";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import BackspaceRoundedIcon from "@mui/icons-material/BackspaceRounded";

import StripeCheckoutButton2025 from "./StripeCheckoutButton2025";
import { AppThemeContext } from "./AppShell";

const PAYMENT_PHASE = "4";
const TITLE = "マスターマインドメンバーズ2026";
const DESCRIPTION = "公演特別クーポンでのお申し込み";

// ひらがな → カタカナ
const toKatakana = (str = "") =>
  str.replace(/[ぁ-ゖ]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) + 0x60)
  );

const formatAmount = (value) => {
  if (value == null) return "-";
  if (typeof value === "number") return `¥${value.toLocaleString()}`;
  if (typeof value === "string") {
    if (value.includes("¥")) return value;
    const n = Number(value.replace(/,/g, ""));
    return isNaN(n) ? value : `¥${n.toLocaleString()}`;
  }
  return String(value);
};

export default function CouponPaymentBox({ onCouponChange, clearSignal = 0 } = {}) {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const textBase = theme.railText || "rgba(30,30,60,0.92)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const surface = theme.surfaceBg || "#FFFFFF";

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // クーポン関連
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // appliedCoupon の変化を親に通知
  useEffect(() => {
    if (typeof onCouponChange === "function") {
      onCouponChange(appliedCoupon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCoupon]);

  const isComposingRef = useRef(false); // IME合成中フラグ
  const [useVirtualKeyboard, setUseVirtualKeyboard] = useState(false);
  const [keySize, setKeySize] = useState("M"); // S / M / L

  // paymentPhase（priceId取得用）
  const [paymentPhase, setPaymentPhase] = useState(null);


  // localStorage のキー（ユーザーごとに分離）
  const storageKey = userData?.userId
    ? `mm2026_appliedCoupon_${userData.userId}`
    : null;

  // ユーザーデータ取得
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      return;
    }
    const userDocRef = doc(db, "users", currentUser.uid);
    const unsub = onSnapshot(
      userDocRef,
      (snap) => {
        if (snap.exists()) {
          setUserData({ ...snap.data(), userId: currentUser.uid });
        }
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  // paymentPhases2026 から priceId を取得（visibility無視）
  // ラベル "MMM2026" のフェーズを優先、なければ最初の1件にフォールバック
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "paymentPhases2026"),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => r.priceIdTaxIncluded || r.priceIdTaxExcluded);
        if (list.length === 0) return;

        const mmm2026 = list.find((r) => r.label === "MMM2026");
        setPaymentPhase(mmm2026 || list[0]);
      }
    );
    return () => unsub();
  }, []);

  // クーポン検証の共通処理
  // silent=true で呼ぶとエラーメッセージは出さず、結果だけ返す（自動再適用用）
  const validateCoupon = async (code, { silent = false } = {}) => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return null;

    const snap = await getDocs(
      query(
        collection(db, "coupons"),
        where("code", "==", trimmed),
        where("active", "==", true)
      )
    );
    if (snap.empty) {
      if (!silent) setCouponError("無効なクーポンコードです");
      return null;
    }
    const couponData = snap.docs[0].data();
    const now = Date.now();
    const start = couponData.start?.toMillis?.() ?? 0;
    const end = couponData.end?.toMillis?.() ?? Infinity;
    if (now < start) {
      if (!silent) setCouponError("このクーポンはまだ有効期間外です");
      return null;
    }
    if (now > end) {
      if (!silent) setCouponError("このクーポンは有効期限が切れています");
      return null;
    }
    if (couponData.maxUses && couponData.usedCount >= couponData.maxUses) {
      if (!silent) setCouponError("このクーポンは利用上限に達しています");
      return null;
    }
    return {
      id: snap.docs[0].id,
      code: couponData.code,
      eventName: couponData.eventName,
      discountPercent: couponData.discountPercent,
      // 旧フィールド（後方互換）
      originalPrice: couponData.originalPrice,
      discountedPrice: couponData.discountedPrice,
      originalPriceOverseas: couponData.originalPriceOverseas,
      discountedPriceOverseas: couponData.discountedPriceOverseas,
      stripePromotionCodeId: couponData.stripePromotionCodeId,
      end: couponData.end,
    };
  };

  // ユーザー入力からの適用
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const result = await validateCoupon(couponCode);
      if (result) {
        setAppliedCoupon(result);
        // 履歴を localStorage に保存
        if (storageKey) {
          localStorage.setItem(
            storageKey,
            JSON.stringify({ code: result.code, appliedAt: Date.now() })
          );
        }
      }
    } catch (err) {
      console.error("クーポン検証エラー:", err);
      setCouponError("エラーが発生しました。もう一度お試しください");
    }
    setCouponLoading(false);
  };

  // 起動時: localStorage から履歴を復元
  useEffect(() => {
    if (!storageKey) return;
    const saved = localStorage.getItem(storageKey);
    if (!saved) return;
    try {
      const { code } = JSON.parse(saved);
      if (!code) return;
      // サイレントに再検証（失効してたら何もしない）
      validateCoupon(code, { silent: true }).then((result) => {
        if (result) {
          setAppliedCoupon(result);
        } else {
          // 無効/失効 → 履歴を掃除
          localStorage.removeItem(storageKey);
        }
      });
    } catch (_) {
      localStorage.removeItem(storageKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const handleRemoveCoupon = () => {
    // 履歴も削除
    if (storageKey) localStorage.removeItem(storageKey);
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  // 親からの解除シグナルを受け取る
  useEffect(() => {
    if (clearSignal > 0) {
      handleRemoveCoupon();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSignal]);

  // ローディング
  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!userData) return null;

  // 4期支払い済みガード
  const payment = userData.payments?.find((p) => p.paymentPhase === PAYMENT_PHASE);
  if (payment?.paymentMethod === "銀行振込" || payment?.paymentMethod === "クレジットカード") {
    return (
      <Box
        sx={{
          mt: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${accent}10, ${accent}24)`,
          border: `1px solid ${accent}40`,
          textAlign: "center",
          maxWidth: 680,
          mx: "auto",
        }}
      >
        <CelebrationIcon sx={{ fontSize: 44, color: accent, mb: 1 }} />
        <Typography variant="h6" fontWeight={800} sx={{ color: textBase }}>
          2026年度マスターマインドへの参加が確定しています
        </Typography>
      </Box>
    );
  }

  const isOverseas = userData.prefecture?.includes("海外");
  const formattedPhone = userData.registrationPhone
    ? userData.registrationPhone.slice(-4)
    : "----";

  // 銀行振込受付中画面は PaymentPhases2026 が表示するのでここでは何もしない

  // クーポン未適用: 入力欄のみ表示
  if (!appliedCoupon) {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          maxWidth: 480,
          mx: "auto",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
          <LocalActivityRoundedIcon sx={{ color: accent, fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textBase }}>
            クーポンコードをお持ちの方
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          sx={{ color: "rgba(0,0,0,0.6)", mb: 2, lineHeight: 1.7 }}
        >
          公演で配布されたクーポンコードをお持ちの方は、下記に入力してお申し込みいただけます。
        </Typography>

        {!couponOpen ? (
          <Button
            variant="outlined"
            onClick={() => setCouponOpen(true)}
            sx={{
              borderColor: accent,
              color: accent,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            クーポンコードを入力する
          </Button>
        ) : (
          <Collapse in={couponOpen}>
            {useVirtualKeyboard ? (
              // ─── 仮想キーボードモード ───
              (() => {
                const sizeConfig = {
                  S: { cols: 10, height: 36, fontSize: 13 },
                  M: { cols: 7, height: 48, fontSize: 16 },
                  L: { cols: 5, height: 60, fontSize: 20 },
                }[keySize];

                return (
                  <Box>
                    {/* 入力中の表示 */}
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${border}`,
                        bgcolor: "rgba(0,0,0,0.02)",
                        minHeight: 44,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          letterSpacing: "0.18em",
                          color: couponCode ? accent : "rgba(0,0,0,0.35)",
                          fontSize: 18,
                          wordBreak: "break-all",
                        }}
                      >
                        {couponCode || "コードを入力"}
                      </Typography>
                    </Box>
                    {couponError && (
                      <Typography
                        variant="caption"
                        sx={{ color: "#d32f2f", display: "block", mb: 1 }}
                      >
                        {couponError}
                      </Typography>
                    )}

                    {/* サイズ切替 */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(0,0,0,0.5)", alignSelf: "center", mr: 0.5 }}
                      >
                        キーの大きさ:
                      </Typography>
                      {["S", "M", "L"].map((s) => (
                        <Button
                          key={s}
                          size="small"
                          variant={keySize === s ? "contained" : "outlined"}
                          onClick={() => setKeySize(s)}
                          sx={{
                            minWidth: 32,
                            px: 1,
                            py: 0.2,
                            fontWeight: 700,
                            textTransform: "none",
                            bgcolor: keySize === s ? accent : "transparent",
                            color: keySize === s ? "#fff" : "rgba(0,0,0,0.6)",
                            borderColor: border,
                            "&:hover": {
                              bgcolor: keySize === s ? accent : "rgba(0,0,0,0.04)",
                              opacity: keySize === s ? 0.9 : 1,
                            },
                          }}
                        >
                          {s}
                        </Button>
                      ))}
                    </Stack>

                    {/* A-Z */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${sizeConfig.cols}, 1fr)`,
                        gap: 0.6,
                        mb: 1,
                      }}
                    >
                      {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((ch) => (
                        <Button
                          key={ch}
                          variant="outlined"
                          onClick={() => setCouponCode((c) => c + ch)}
                          sx={{
                            minWidth: 0,
                            p: 0,
                            height: sizeConfig.height,
                            fontSize: sizeConfig.fontSize,
                            fontWeight: 700,
                            fontFamily: "monospace",
                            color: textBase,
                            borderColor: border,
                          }}
                        >
                          {ch}
                        </Button>
                      ))}
                    </Box>

                    {/* 0-9 */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${sizeConfig.cols}, 1fr)`,
                        gap: 0.6,
                        mb: 1.5,
                      }}
                    >
                      {"0123456789".split("").map((n) => (
                        <Button
                          key={n}
                          variant="outlined"
                          onClick={() => setCouponCode((c) => c + n)}
                          sx={{
                            minWidth: 0,
                            p: 0,
                            height: sizeConfig.height,
                            fontSize: sizeConfig.fontSize,
                            fontWeight: 700,
                            fontFamily: "monospace",
                            color: accent,
                            borderColor: `${accent}60`,
                          }}
                        >
                          {n}
                        </Button>
                      ))}
                    </Box>

                    {/* アクション（縦に1列ずつ） */}
                    <Stack spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<BackspaceRoundedIcon />}
                        onClick={() => setCouponCode((c) => c.slice(0, -1))}
                        disabled={!couponCode}
                        sx={{
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          height: 44,
                          fontWeight: 600,
                        }}
                      >
                        1文字削除
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setCouponCode("")}
                        disabled={!couponCode}
                        sx={{
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          height: 44,
                          fontWeight: 600,
                        }}
                      >
                        全部消す
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        sx={{
                          bgcolor: accent,
                          fontWeight: 700,
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          height: 48,
                          fontSize: 15,
                          "&:hover": { bgcolor: accent, opacity: 0.9 },
                        }}
                      >
                        {couponLoading ? "..." : "クーポンを適用"}
                      </Button>
                    </Stack>
                  </Box>
                );
              })()
            ) : (
              // ─── 通常入力モード ───
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  placeholder="コードを入力（英字・数字）"
                  value={couponCode}
                  onChange={(e) => {
                    if (isComposingRef.current) {
                      setCouponCode(e.target.value);
                      return;
                    }
                    const filtered = e.target.value
                      .replace(/[^A-Za-z0-9]/g, "")
                      .toUpperCase();
                    setCouponCode(filtered);
                  }}
                  onCompositionStart={() => {
                    isComposingRef.current = true;
                  }}
                  onCompositionEnd={(e) => {
                    isComposingRef.current = false;
                    const filtered = e.target.value
                      .replace(/[^A-Za-z0-9]/g, "")
                      .toUpperCase();
                    setCouponCode(filtered);
                  }}
                  error={!!couponError}
                  helperText={couponError}
                  sx={{ flex: 1 }}
                  type="email"
                  name="coupon-code"
                  inputProps={{
                    inputMode: "email",
                    autoCapitalize: "characters",
                    autoCorrect: "off",
                    autoComplete: "one-time-code",
                    spellCheck: false,
                    lang: "en",
                    pattern: "[A-Za-z0-9]*",
                  }}
                  InputProps={{
                    sx: {
                      fontFamily: "monospace",
                      letterSpacing: "0.12em",
                      fontSize: 14,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  sx={{
                    minWidth: 72,
                    bgcolor: accent,
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: accent, opacity: 0.9 },
                  }}
                >
                  {couponLoading ? "..." : "適用"}
                </Button>
              </Stack>
            )}

            {/* キーボード切替リンク */}
            <Button
              size="small"
              startIcon={<KeyboardRoundedIcon fontSize="small" />}
              onClick={() => {
                setUseVirtualKeyboard((v) => !v);
                setCouponCode("");
                setCouponError("");
              }}
              sx={{
                mt: 1,
                color: "rgba(0,0,0,0.55)",
                textTransform: "none",
                fontSize: 12,
              }}
            >
              {useVirtualKeyboard
                ? "通常のキーボードに切り替える"
                : "うまく入力できない方はこちら（仮想キーボード）"}
            </Button>
          </Collapse>
        )}
      </Paper>
    );
  }

  // クーポン適用済み: 決済UI表示
  if (!paymentPhase) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // 割引率(%)
  const pct = Number(appliedCoupon.discountPercent);
  const validPct = Number.isFinite(pct) && pct >= 0 && pct <= 100 ? pct : 0;

  // 失効までの日数計算
  const expiryInfo = (() => {
    const endMs = appliedCoupon.end?.toMillis?.();
    if (!endMs) return null;
    const diffMs = endMs - Date.now();
    if (diffMs <= 0) return { expired: true };
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (days >= 1) return { days, label: `あと${days}日で失効` };
    if (hours >= 1) return { days: 0, label: `あと${hours}時間で失効` };
    return { days: 0, label: "本日失効" };
  })();

  return (
    <Box sx={{ mt: 3, maxWidth: 520, mx: "auto" }}>
      {/* クーポン適用表示 */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: `1px solid ${accent}40`,
          bgcolor: `${accent}0A`,
          mb: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalActivityRoundedIcon sx={{ color: accent, fontSize: 20 }} />
            <Box>
              <Typography variant="caption" sx={{ color: accent, fontWeight: 700 }}>
                クーポン適用中
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: textBase,
                }}
              >
                {appliedCoupon.code}
              </Typography>
              {appliedCoupon.eventName && (
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
                  {appliedCoupon.eventName}
                </Typography>
              )}
            </Box>
          </Stack>
          <Button
            size="small"
            onClick={handleRemoveCoupon}
            sx={{
              color: "rgba(0,0,0,0.55)",
              textTransform: "none",
              fontSize: 12,
            }}
          >
            解除
          </Button>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#e53935" }}>
            {validPct}% OFF
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
            （下のプランに自動で割引が適用されます）
          </Typography>
        </Stack>

        {/* 失効までのカウントダウン */}
        {expiryInfo && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: expiryInfo.days <= 3 ? "#e53935" : "rgba(0,0,0,0.6)",
              fontWeight: expiryInfo.days <= 3 ? 700 : 500,
            }}
          >
            ⏰ {expiryInfo.label}
          </Typography>
        )}
      </Paper>

      {/* 案内（決済は下のブロックで行う） */}
      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          color: "rgba(0,0,0,0.65)",
          fontWeight: 600,
          mt: 1,
        }}
      >
        下のお申し込みプランからお手続きください ↓
      </Typography>
    </Box>
  );
}
